// 飞书跳转认证封装 (与 xcs-cms 同源).
// 通过 @makeblock/passport-client SDK 接管登录态:
// 1. 浏览器跳转到 client.url 完成飞书 OAuth → 重定向回 client.redirect
// 2. 回跳后 SDK 自动把 utoken 写入根域 cookie (.makeblock.com / .xtool.com; localhost / 局域网 IP 走 localStorage)
// 3. 业务请求由 request.ts 注入 utoken header, 服务端 account.AuthUToken 中间件校验
//
// 平台标识使用 XTOOL_PLATFORM.MATERIALS_LAB (材料/工艺参数平台, 已在 SDK 枚举中)

import PassportClient, {
    AREA,
    CLIENT_ENV,
    DOMAIN_SPACE,
    PAGE_MODE,
    XTOOL_PLATFORM,
} from '@makeblock/passport-client';

const env = (import.meta.env.VITE_PASSPORT_ENV || 'dev') as
    | 'dev'
    | 'test'
    | 'test_us'
    | 'pre_us'
    | 'prod-cn'
    | 'prod-us'
    | 'prod-eu';

// 本地开发源 (localhost / 127.0.0.1 / 局域网 IP 开发服务器) 共享不了 passport 写的根域 cookie.
// SDK 只在构造那一刻看 URL 里有没有 uls 来决定存储策略 (有 = localStorage, 没有 = cookie; 之后 setter 改不回来),
// 而 cookie 策略带 secure 标记, 在 http://<IP> 上写不进去 → 每次启动都没 token → 和 passport 之间来回跳.
// 所以构造前先把 uls=1 放进 location.search (HashRouter 的路由在 # 后, 不受影响).
// 只认 localhost / IP: 正式域名 (*.makeblock.com / *.xtool.com) 都与 passport 同根域, 必须走 cookie ——
// 曾按"非 *.makeblock.com"判定, 美服 xtool.com 的页面被误走 localStorage, 登录后读不到 token 来回跳 (2026-10-22).
const FOREIGN_ORIGIN =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
        /^\d{1,3}(\.\d{1,3}){3}$/.test(window.location.hostname) ||
        window.location.hostname.includes(':'));
// 正式域名反过来要把残留的 uls 去掉: SDK 见到 uls 就走 localStorage, 而 goToLogin 拿当前地址当回跳地址,
// uls 会随回跳一直带着 → 永远读不到 cookie 里的 token, 来回跳 (旧版误判留下的地址 / 书签都会触发).
if (typeof window !== 'undefined' && FOREIGN_ORIGIN !== new URLSearchParams(window.location.search).has('uls')) {
    const u = new URL(window.location.href);
    if (FOREIGN_ORIGIN) u.searchParams.set('uls', '1');
    else u.searchParams.delete('uls');
    window.history.replaceState(window.history.state, '', u.toString());
}

export const client = new PassportClient({
    domainSpace: DOMAIN_SPACE.XTOOL,
    platform: XTOOL_PLATFORM.MATERIALS_LAB,
    mode: PAGE_MODE.PAGE,
    area: AREA.CN,
    lang: 'zh',
} as any);

// 登录页地址也带上 uls=1 (SDK 的 useLocalstorage 只影响登录页参数, 存储策略见上面构造前那段)
if (FOREIGN_ORIGIN) {
    (client as any).useLocalstorage = true;
}

switch (env) {
    case 'prod-cn':
        client.env = CLIENT_ENV.PROD;
        client.lang = 'zh';
        client.area = AREA.CN;
        break;
    case 'prod-us':
        client.env = CLIENT_ENV.PROD;
        client.lang = 'en';
        client.area = AREA.US;
        break;
    case 'prod-eu':
        client.env = CLIENT_ENV.PROD;
        client.lang = 'en';
        client.area = AREA.EU;
        break;
    case 'test':
        client.env = CLIENT_ENV.TEST;
        client.lang = 'zh';
        client.area = AREA.CN;
        break;
    case 'test_us':
        client.env = CLIENT_ENV.TEST_US;
        client.lang = 'en';
        client.area = AREA.US;
        break;
    case 'pre_us':
        client.env = CLIENT_ENV.PRE;
        client.lang = 'en';
        client.area = AREA.US;
        break;
    case 'dev':
    default:
        client.env = CLIENT_ENV.DEV;
        client.lang = 'zh';
        client.area = AREA.CN;
        break;
}

export interface UserInfo {
    userName?: string;
    name?: string;
    email?: string;
    [key: string]: any;
}

// 本地开发旁路: 设置 REACT_APP_BYPASS_AUTH=true 时跳过飞书登录, mock 一个固定用户.
// 与后端 BYPASS_AUTH=true 配套使用.
const BYPASS = import.meta.env.VITE_BYPASS_AUTH === 'true';
const MOCK_USER: UserInfo = {
    userName: 'dev-local',
    name: '本地开发用户',
    email: 'dev-local@example.com',
};

export const auth = {
    /** 当前 utoken; 由 SDK 从 cookie/localStorage 读取. Bypass 模式返回固定占位 token. */
    getUToken(): string {
        if (BYPASS) return 'bypass';
        return (client as any).token || '';
    },

    /** 是否已登录. Bypass 模式恒为 true. */
    isLoggedIn(): boolean {
        if (BYPASS) return true;
        return Boolean(this.getUToken());
    },

    /** 跳到 passport 登录页, 登录成功后回跳当前页. Bypass 模式下不做任何事. */
    goToLogin(redirect?: string): void {
        if (BYPASS) return;
        (client as any).token = '';
        (client as any).redirect = redirect || window.location.href;
        window.location.href = (client as any).url;
    },

    /** 拉一次用户信息. Bypass 模式返回 mock 用户. */
    async getUserInfo(): Promise<UserInfo> {
        if (BYPASS) return MOCK_USER;
        return (client as any).userInfo();
    },

    /** 退出登录: 清 token + 跳回登录页. Bypass 模式不做事. */
    async logout(): Promise<void> {
        if (BYPASS) return;
        try {
            await (client as any).logout();
        } finally {
            this.goToLogin();
        }
    },
};
